import { create } from 'zustand'

/** Long enough that a fast refetch still reads as a switch rather than a flicker. */
const MIN_TRANSITION_MS = 500
/** The switch waits on every refetch on the page; past this, each page shows its own loading. */
const MAX_TRANSITION_MS = 1000

export type SwitchedWorkspace =
  | { kind: 'personal' }
  | { kind: 'team'; id: string; name?: string; avatarUrl?: string }

interface WorkspaceSwitchFeedbackState {
  switchedTo: SwitchedWorkspace | null
  previousWorkspaceId: string | null
  pendingSwitch: Promise<unknown>
  pendingTransitions: number
}

export const useWorkspaceSwitchFeedbackStore = create<WorkspaceSwitchFeedbackState>(() => ({
  switchedTo: null,
  previousWorkspaceId: null,
  pendingSwitch: Promise.resolve(),
  pendingTransitions: 0,
}))

export function openWorkspaceSwitchedDialog({
  switchedTo,
  previousWorkspaceId,
  pendingSwitch,
}: {
  switchedTo: SwitchedWorkspace
  previousWorkspaceId: string | null
  pendingSwitch: Promise<unknown>
}) {
  useWorkspaceSwitchFeedbackStore.setState({ switchedTo, previousWorkspaceId, pendingSwitch })
}

export function closeWorkspaceSwitchedDialog() {
  useWorkspaceSwitchFeedbackStore.setState({ switchedTo: null, previousWorkspaceId: null })
}

/** Covers the page until `work` settles, for at least `MIN_TRANSITION_MS` and at most `MAX_TRANSITION_MS`. */
export function showWorkspaceTransition(work: Promise<unknown>) {
  useWorkspaceSwitchFeedbackStore.setState(state => ({
    pendingTransitions: state.pendingTransitions + 1,
  }))
  const minimum = Promise.withResolvers<void>()
  const maximum = Promise.withResolvers<void>()
  setTimeout(minimum.resolve, MIN_TRANSITION_MS)
  setTimeout(maximum.resolve, MAX_TRANSITION_MS)
  void Promise.race([Promise.allSettled([work, minimum.promise]), maximum.promise]).then(() => {
    useWorkspaceSwitchFeedbackStore.setState(state => ({
      pendingTransitions: state.pendingTransitions - 1,
    }))
  })
}
